import { createApi } from '@reduxjs/toolkit/query/react';
import { dynamicBaseQuery } from './baseApi';
import type {
  ApiResponse,
  UserStore,
  UserOutlet,
  AssignUserToStoreData,
  UpdateUserRoleData,
  AssignUserToOutletData,
  BulkAssignUserToOutletsData,
  UserStoreQueryParams,
  UserOutletQueryParams,
  PaginationMeta,
} from '@/types';

interface UserStoresResponse {
  users: UserStore[];
  pagination: PaginationMeta;
}

interface StoresForUserResponse {
  stores: UserStore[];
  pagination: PaginationMeta;
}

interface UserOutletsResponse {
  users: UserOutlet[];
  pagination: PaginationMeta;
}

interface OutletsForUserResponse {
  outlets: UserOutlet[];
  pagination: PaginationMeta;
}

interface BulkAssignResponse {
  assigned: number;
  skipped: number;
}

export const userAssignmentApi = createApi({
  reducerPath: 'userAssignmentApi',
  baseQuery: dynamicBaseQuery,
  tagTypes: ['UserStore', 'UserOutlet'],
  endpoints: (builder) => ({
    // UserStore Endpoints
    getUsersInStore: builder.query<UserStoresResponse, { storeId: string } & UserStoreQueryParams>({
      query: ({ storeId, ...params }) => ({
        url: `/stores/${storeId}/users`,
        params,
      }),
      transformResponse: (response: ApiResponse<UserStoresResponse>) => response.data,
      providesTags: (_result, _error, { storeId }) => [{ type: 'UserStore', id: storeId }],
    }),

    getStoresForUser: builder.query<StoresForUserResponse, { userId: string } & UserStoreQueryParams>({
      query: ({ userId, ...params }) => ({
        url: `/users/${userId}/stores`,
        params,
      }),
      transformResponse: (response: ApiResponse<StoresForUserResponse>) => response.data,
      providesTags: (_result, _error, { userId }) => [{ type: 'UserStore', id: userId }],
    }),

    assignUserToStore: builder.mutation<UserStore, { storeId: string; data: AssignUserToStoreData }>({
      query: ({ storeId, data }) => ({
        url: `/stores/${storeId}/users`,
        method: 'POST',
        body: data,
      }),
      transformResponse: (response: ApiResponse<UserStore>) => response.data,
      invalidatesTags: (_result, _error, { storeId }) => [{ type: 'UserStore', id: storeId }],
    }),

    updateUserRole: builder.mutation<UserStore, { storeId: string; userId: string; data: UpdateUserRoleData }>({
      query: ({ storeId, userId, data }) => ({
        url: `/stores/${storeId}/users/${userId}/role`,
        method: 'PATCH',
        body: data,
      }),
      transformResponse: (response: ApiResponse<UserStore>) => response.data,
      invalidatesTags: (_result, _error, { storeId }) => [{ type: 'UserStore', id: storeId }],
    }),

    removeUserFromStore: builder.mutation<void, { storeId: string; userId: string }>({
      query: ({ storeId, userId }) => ({
        url: `/stores/${storeId}/users/${userId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { storeId }) => [
        { type: 'UserStore', id: storeId },
        { type: 'UserOutlet', id: storeId },
      ],
    }),

    activateUserInStore: builder.mutation<UserStore, { storeId: string; userId: string }>({
      query: ({ storeId, userId }) => ({
        url: `/stores/${storeId}/users/${userId}/activate`,
        method: 'PATCH',
      }),
      transformResponse: (response: ApiResponse<UserStore>) => response.data,
      invalidatesTags: (_result, _error, { storeId }) => [{ type: 'UserStore', id: storeId }],
    }),

    deactivateUserInStore: builder.mutation<UserStore, { storeId: string; userId: string }>({
      query: ({ storeId, userId }) => ({
        url: `/stores/${storeId}/users/${userId}/deactivate`,
        method: 'PATCH',
      }),
      transformResponse: (response: ApiResponse<UserStore>) => response.data,
      invalidatesTags: (_result, _error, { storeId }) => [{ type: 'UserStore', id: storeId }],
    }),

    // UserOutlet Endpoints
    getUsersInOutlet: builder.query<UserOutletsResponse, { outletId: string } & UserOutletQueryParams>({
      query: ({ outletId, ...params }) => ({
        url: `/outlets/${outletId}/users`,
        params,
      }),
      transformResponse: (response: ApiResponse<UserOutletsResponse>) => response.data,
      providesTags: (_result, _error, { outletId }) => [{ type: 'UserOutlet', id: outletId }],
    }),

    getOutletsForUser: builder.query<OutletsForUserResponse, { userId: string } & UserOutletQueryParams>({
      query: ({ userId, ...params }) => ({
        url: `/users/${userId}/outlets`,
        params,
      }),
      transformResponse: (response: ApiResponse<OutletsForUserResponse>) => response.data,
      providesTags: (_result, _error, { userId }) => [{ type: 'UserOutlet', id: userId }],
    }),

    assignUserToOutlet: builder.mutation<UserOutlet, { outletId: string; data: AssignUserToOutletData }>({
      query: ({ outletId, data }) => ({
        url: `/outlets/${outletId}/users`,
        method: 'POST',
        body: data,
      }),
      transformResponse: (response: ApiResponse<UserOutlet>) => response.data,
      invalidatesTags: (_result, _error, { outletId }) => [{ type: 'UserOutlet', id: outletId }],
    }),

    bulkAssignUserToOutlets: builder.mutation<BulkAssignResponse, { userId: string; data: BulkAssignUserToOutletsData }>({
      query: ({ userId, data }) => ({
        url: `/users/${userId}/outlets/bulk-assign`,
        method: 'POST',
        body: data,
      }),
      transformResponse: (response: ApiResponse<BulkAssignResponse>) => response.data,
      invalidatesTags: (_result, _error, { userId }) => [{ type: 'UserOutlet', id: userId }],
    }),

    removeUserFromOutlet: builder.mutation<void, { outletId: string; userId: string }>({
      query: ({ outletId, userId }) => ({
        url: `/outlets/${outletId}/users/${userId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { outletId }) => [{ type: 'UserOutlet', id: outletId }],
    }),

    activateUserInOutlet: builder.mutation<UserOutlet, { outletId: string; userId: string }>({
      query: ({ outletId, userId }) => ({
        url: `/outlets/${outletId}/users/${userId}/activate`,
        method: 'PATCH',
      }),
      transformResponse: (response: ApiResponse<UserOutlet>) => response.data,
      invalidatesTags: (_result, _error, { outletId }) => [{ type: 'UserOutlet', id: outletId }],
    }),

    deactivateUserInOutlet: builder.mutation<UserOutlet, { outletId: string; userId: string }>({
      query: ({ outletId, userId }) => ({
        url: `/outlets/${outletId}/users/${userId}/deactivate`,
        method: 'PATCH',
      }),
      transformResponse: (response: ApiResponse<UserOutlet>) => response.data,
      invalidatesTags: (_result, _error, { outletId }) => [{ type: 'UserOutlet', id: outletId }],
    }),
  }),
});

export const {
  // UserStore hooks
  useGetUsersInStoreQuery,
  useGetStoresForUserQuery,
  useAssignUserToStoreMutation,
  useUpdateUserRoleMutation,
  useRemoveUserFromStoreMutation,
  useActivateUserInStoreMutation,
  useDeactivateUserInStoreMutation,
  // UserOutlet hooks
  useGetUsersInOutletQuery,
  useGetOutletsForUserQuery,
  useAssignUserToOutletMutation,
  useBulkAssignUserToOutletsMutation,
  useRemoveUserFromOutletMutation,
  useActivateUserInOutletMutation,
  useDeactivateUserInOutletMutation,
} = userAssignmentApi;
